import React, { useState, useEffect, useCallback } from "react";
import {
  ShoppingCart,
  Tag,
  Package,
  Search,
  User,
  Plus,
  Minus,
  Receipt,
  CreditCard,
  Phone,
  Star,
  Clock,
  Trash2,
  X,
  Loader,
  Edit2,
  ImagePlus,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  FormikSearchSelect,
  FormikInput,
  FormikSearchSectionSelect,
} from "../../components/formik-components";
import { Formik } from "formik";
import Common from "../../helper/common";
import {
  ClubPrivacyTypeEnum,
  PaymentStatusEnum,
  respEnum,
  SaleOrderStatusEnum,
  AppUserSettingsTypeEnum,
  ImageFolderEnum,
} from "../../constants/appConstant";
import AddCustomer from "../clubCustomers/components/addCustomer";
import Select from "react-select";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AppInput, AppSearchSectionSelect } from "../../components/appComponents";

const POSSystem = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(PaymentStatusEnum.Paid);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [receivedAmount, setReceivedAmount] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [attachment, setAttachment] = useState();
  const [receiptEnable, setReceiptEnable] = useState(false);

  const currentDate = Common.moment().utc();
  const [orderDate, setOrderDate] = useState(currentDate.format());

  // Get dynamic date restrictions based on appUserSettings
  const getDateRestrictions = () => {
    const posOrderDateSetting = global.user.appUserSettings?.find(
      setting => setting.type === AppUserSettingsTypeEnum.POS_Order_Date_Selection
    );
    const hoursBack = posOrderDateSetting?.value || 24; // Default 24 hours (1 days)

    // Convert hours to days (round up to ensure we don't go beyond the limit)
    const daysBack = Math.max(1, Math.min(30, Math.ceil(hoursBack / 24)));
    const minDate = Common.moment().utc().subtract(daysBack, "days").local().format("YYYY-MM-DD");
    const maxDate = Common.moment().utc().local().format("YYYY-MM-DD");
    return { minDate, maxDate, daysBack };
  };




  // Data states
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);

  // Modal state
  const [openCustomerModal, setOpenCustomerModal] = useState(false);

  // Get image URL helper
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    let imageUrl = Common.Utility.getImagePath(imagePath);
    return imageUrl;
  };

  // Default category and product icons
  const getDefaultCategoryIcon = (categoryName) => {
    if (!categoryName || typeof categoryName !== "string") return "📦";

    const icons = {
      main: "🍽️",
      appetizer: "🥗",
      drinks: "🥤",
      desserts: "🍰",
      all: "🛍️",
    };
    return icons[categoryName.toLowerCase()] || "📦";
  };

  const getDefaultProductIcon = (categoryName) => {
    if (!categoryName || typeof categoryName !== "string") return "🍽️";

    const icons = {
      main: "🍛",
      appetizer: "🥗",
      drinks: "🥤",
      desserts: "🍰",
    };
    return icons[categoryName.toLowerCase()] || "🛒";
  };

  const getCategories = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetProductCategories"
      );
      if (response?.status === respEnum.Success) {
        const categoryData = [
          { id: "all", name: "All", image: null },
          ...response.data.map((cat) => ({
            id: cat.id,
            name: cat.name,
            image: cat.image || null,
          })),
        ];
        setCategories(categoryData);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
  };

  const getProductsForPOS = async (categoryId = null) => {
    try {
      debugger;
      setProductsLoading(true);
      let url = "GetProductsForPOS";
      if (categoryId !== "all" && categoryId !== null) {
        url += `?categoryId=${categoryId}`;
      }

      const response = await Common.ApiService.getInstance().request(
        url,
        {},
        "Get",
        false
      );

      if (response?.status === respEnum.Success) {
        const productData = response.data || [];
        setProducts(
          productData.map((product) => ({
            id: product.id,
            name: product.name,
            regular_price: product.regular_price || 0,
            special_price: product.special_price || 0,
            price: product.effectivePrice || product.regular_price || 0,
            category: product.categoryid || "main",
            categoryName: product.categoryName || "",
            stock: product.current_stock || 0,
            image: product.thumbnail,
            description: product.desc,
            isLowStock: product.isLowStock === 1,
            sortOrder: product.sortOrder,
            isdisplayontop: product.isdisplayontop,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setProductsLoading(false);
    }
  };

  const getCustomers = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "ClubPlayersddl",
        {},
        "Get",
        false
      );
      const customerData = response?.data ?? [];

      const updatedCustomerList = [
        { id: "", name: "Select Customer", phone: "" },
        ...customerData.map((customer) => ({
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
        })),
      ];
      setCustomers(updatedCustomerList);
      return updatedCustomerList;
    } catch (error) {
      console.error("Error fetching customers:", error);
      const fallback = [{ id: "", name: "Select Customer", phone: "" }];
      setCustomers(fallback);
      return fallback;
    }
  };

  const getPaymentAccounts = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetClubPaymentTypes"
      );
      if (response?.status === respEnum.Success) {
        setPaymentAccounts(response?.data ?? []);
      }
    } catch (error) {
      console.error("Error fetching payment accounts:", error);
      setPaymentAccounts([]);
    }
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([
        getCategories(),
        getCustomers(),
        getPaymentAccounts(),
      ]);
      await getProductsForPOS(); // Load all products initially
      setLoading(false);
    };
    initializeData();
  }, []);

  // Handle category change
  const handleCategoryChange = async (categoryId) => {
    debugger;
    setSelectedCategory(categoryId);
    await getProductsForPOS(categoryId);
  };

  // Update total when payment status changes to Paid
  useEffect(() => {
    if (paymentStatus === PaymentStatusEnum.Paid && total > 0) {
      const netTotal = Math.max(0, total - (discountAmount || 0));
      setReceivedAmount(netTotal.toString());
      // setReceivedAmount(total.toString());
    }
  }, [total, paymentStatus, discountAmount]);

  // Filter products by search term only (category filtering is done by API)
  const filteredProducts = products
    .filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      // Sort by displayontop first, then by sortOrder, then by name
      if (b.isdisplayontop !== a.isdisplayontop) {
        return b.isdisplayontop - a.isdisplayontop;
      }
      if (a.sortOrder !== b.sortOrder) {
        return (a.sortOrder || 999) - (b.sortOrder || 999);
      }
      return a.name.localeCompare(b.name);
    });

  const selectedCustomerData = customers.find(
    (c) => c.id === selectedCustomer?.value
  );

  // Cart functions
  const addToCart = (product) => {
    if (product.stock <= 0) {
      toast.error("Product is out of stock!");
      return;
    }

    const existingItem = cart.find((item) => item.id === product.id);
    if (existingItem) {
      if (existingItem.quantity >= product.stock) {
        toast.error("Stock limit reached!");
        return;
      }
      setCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setTotal((prev) => prev + product.price);
  };

  const removeFromCart = (product) => {
    const existingItem = cart.find((item) => item.id === product.id);
    if (existingItem.quantity === 1) {
      setCart(cart.filter((item) => item.id !== product.id));
    } else {
      setCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity - 1 }
            : item
        )
      );
    }
    setTotal((prev) => prev - product.price);
  };

  const removeItemFromCart = (productId) => {
    const item = cart.find((item) => item.id === productId);
    if (item) {
      setTotal((prev) => prev - item.price * item.quantity);
      setCart(cart.filter((item) => item.id !== productId));
    }
  };

  debugger;
  // Customer modal callback
  const _onCustomerAdded = useCallback(
    async (id, phone) => {
      const freshResponse = await getCustomers();
      // Set the newly added customer as selected
      const newCustomer = {
        value: id,
        label: freshResponse.find((c) => c.id === id)?.name || "New Customer",
        phone: phone,
      };

      setSelectedCustomer(newCustomer);


      setOpenCustomerModal(false);
    },

    []
  );
  // Payment status change handler (same logic as addBooking)
  const handlePaymentStatusChange = (status) => {
    setPaymentStatus(status);

    const netTotal = Math.max(0, total - (discountAmount || 0));

    if (status === PaymentStatusEnum.Paid) {
      // Set received amount to total for paid orders
      setReceivedAmount(netTotal.toString());
    } else if (status === PaymentStatusEnum.Partial_Paid) {
      // Clear received amount for partial payments
      setReceivedAmount("");
    } else if (status === PaymentStatusEnum.Un_Paid) {
      // Clear account and received amount for unpaid orders
      setSelectedAccount(null);
      setReceivedAmount("");

    }
  };

  // Handle received amount change for partial payments (same validation as addBooking)
  const handleReceivedAmountChange = (value) => {
    const IntMinLevel = 1;

    if (value < IntMinLevel && value !== "") {
      setReceivedAmount(IntMinLevel.toString());
    } else if (+value >= +total) {
      toast.error(
        `Received Amount cannot be greater than or equal to ${total}`
      );
      setReceivedAmount("");
    } else {
      setReceivedAmount(value);
    }
  };

  const handleDiscountAmountChange = (value) => {
    const IntMinLevel = 1;

    if (value < IntMinLevel && value !== "") {
      setDiscountAmount(IntMinLevel.toString());
    } else if (+value >= +total) {
      toast.error(
        `Discount Amount cannot be greater than or equal to ${total}`
      );
      setDiscountAmount("");
    } else {
      setDiscountAmount(value);
    }
  };


  // Validate payment before placing order (same validation as addBooking)
  const validatePayment = () => {
    if (!selectedCustomer?.value) {
      toast.error("Please select a customer");
      return false;
    }

    if (
      (paymentStatus === PaymentStatusEnum.Paid ||
        paymentStatus === PaymentStatusEnum.Partial_Paid) &&
      !selectedAccount
    ) {
      toast.error("Please Provide the Payment Account for payment");
      return false;
    }

    if (paymentStatus === PaymentStatusEnum.Partial_Paid && !receivedAmount) {
      toast.error("Enter received amount for partial payment");
      return false;
    }

    return true;
  };

  // Create sale order API call
  const createSaleOrder = async (orderData) => {
    try {


      if (attachment && attachment instanceof File) {
        const uploadFilePathType = ImageFolderEnum.upload;
        orderData.attachment = (
          await Common.Utility.handleFileUpload([attachment], uploadFilePathType)
        )[0].photo;
      }

      const response = await Common.ApiService.getInstance().request(
        "CreateSaleOrder",
        orderData,
        "POST"
      );

      if (response?.status === respEnum.Success) {
        setAttachment(null);
        setReceiptEnable(false);
        return { success: true, data: response.data };
      } else {
        return {
          success: false,
          message: response?.message || "Failed to create order",
        };
      }
    } catch (error) {
      console.error("Error creating sale order:", error);
      return {
        success: false,
        message: "Network error occurred while creating order",
      };
    }
  };

  // Handle place order
  const handlePlaceOrder = async (withSlip = false) => {
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (!validatePayment()) {
      return;
    }

    // Validate stock levels before placing order
    const stockValidationError = validateStockLevels();
    if (stockValidationError) {
      toast.error(stockValidationError);
      return;
    }

    setLoading(true);

    try {
      // Prepare order data for API
      const orderData = {
        customer_id: selectedCustomer?.value || null,
        payment_status: paymentStatus,
        total_amount: total,
        discount: +discountAmount || 0,
        // net_amount: total,
        net_amount: Math.max(0, (total || 0) - (discountAmount || 0)),
        // received_amount:
        //   paymentStatus === PaymentStatusEnum.Paid
        //     ? total
        //     : receivedAmount  
        //     ? +receivedAmount
        //     : 0,
        received_amount:
          paymentStatus === PaymentStatusEnum.Paid
            ? Math.max(0, (total || 0) - (discountAmount || 0))
            : +receivedAmount || 0,
        note: withSlip ? "Order with slip requested" : null,
        booking_id: null,
        status: SaleOrderStatusEnum.Confirmed,
        order_date: orderDate,

        // Include club_account_id for paid/partial paid orders
        ...((paymentStatus === PaymentStatusEnum.Paid ||
          paymentStatus === PaymentStatusEnum.Partial_Paid) && {
          club_account_id: selectedAccount?.value || null,
        }),
        items: cart.map((item) => ({
          productid: item.id,
          quantity: item.quantity,
          price: item.price,
          total: item.price * item.quantity,
        })),
      };

      const result = await createSaleOrder(orderData);

      if (result.success) {
        toast.success(
          `Order ${withSlip ? "with slip" : ""} placed successfully!`
        );

        // Navigate to order details if with slip
        if (withSlip && result.data) {
          window.open(
            `/app/pos-order-details?id=${result.data}&print=true`,
            "_blank"
          );
        }

        // Clear cart after successful order
        setCart([]);
        setTotal(0);
        setReceivedAmount("");
        setDiscountAmount("");
        setSelectedCustomer(null);
        setSelectedAccount(null);
        setPaymentStatus(PaymentStatusEnum.Paid);

        // Refresh products to update stock levels
        await getProductsForPOS(selectedCategory);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Failed to place order");
    } finally {
      setLoading(false);
    }
  };

  // Validate stock levels before placing order
  const validateStockLevels = () => {
    for (const item of cart) {
      if (item.quantity > item.stock) {
        return `Insufficient stock for "${item.name}". Available: ${item.stock}, Required: ${item.quantity}`;
      }
    }
    return null;
  };

  // Utility functions
  const getStockColor = (stock, isLowStock = false) => {
    if (stock <= 0) return "text-red-600";
    if (isLowStock || stock <= 10) return "text-orange-500";
    if (stock <= 25) return "text-yellow-600";
    return "text-green-500";
  };

  const getPaymentStatusName = (status) => {
    switch (status) {
      case PaymentStatusEnum.Paid:
        return "Paid";
      case PaymentStatusEnum.Partial_Paid:
        return "Partial Paid";
      case PaymentStatusEnum.Un_Paid:
        return "Unpaid";
      default:
        return "Paid";
    }
  };

  // Prepare customer options for react-select
  const customerOptions = customers.map((customer) => ({
    value: customer.id,
    label: customer.name,
    phone: customer.phone,
  }));

  // Prepare payment account options for react-select
  const paymentAccountOptions = paymentAccounts.map((account) => ({
    value: account.id,
    label: account.name,
  }));

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.isFocused ? "2px solid #2E55B9" : "2px solid #e5e7eb",
      borderRadius: "0.75rem",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(46, 85, 185, 0.2)" : "none",
      "&:hover": {
        border: "2px solid #2E55B9",
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#2E55B9]"></div>
          <p className="mt-4 text-lg font-medium text-gray-600">
            Loading POS System...
          </p>
        </div>
      </div>
    );
  }

  //   const getMinDate = () => {
  //   const today = new Date();
  //   const threeDaysAgo = new Date(today);
  //   threeDaysAgo.setDate(today.getDate() - 3);
  //   return threeDaysAgo.toISOString().split('T')[0];
  // };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header with Quick Actions */}
      {/* <div className="bg-white shadow-sm border-b p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <ShoppingCart className="text-[#2E55B9]" />
              POS System
            </h1>
            <p className="text-gray-600">Point of Sale - Create new orders</p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/app/pos-orders")}
              variant="outline"
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
            >
              <Receipt size={16} className="mr-2" />
              View Orders
            </Button>
            <Button
              onClick={() => navigate("/app/stock-logs")}
              variant="outline"
              className="border-green-200 text-green-600 hover:bg-green-50"
            >
              <Package size={16} className="mr-2" />
              Stock Logs
            </Button>
          </div>
        </div>
      </div> */}

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-6 gap-4 p-4">
        {/* Left Sidebar - Categories */}
        <div className="lg:col-span-1 flex flex-col gap-4">
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-3 bg-gradient-to-r from-[#2E55B9] to-[#4A73D6] text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-base">
                <Tag size={18} />
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="grid grid-cols-1 gap-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    disabled={productsLoading}
                    className={`p-2 rounded-lg flex items-center gap-2 text-left transition-all duration-200 transform hover:scale-105 text-xs disabled:opacity-50
                      ${selectedCategory === cat.id
                        ? "bg-[#2E55B9] text-white shadow-lg scale-105"
                        : "bg-gray-50 hover:bg-[#2E55B9] hover:text-white border border-gray-200"
                      }`}
                  >
                    {productsLoading && selectedCategory === cat.id ? (
                      <Loader className="animate-spin" size={16} />
                    ) : (
                      <>
                        {cat.image ? (
                          <img
                            src={getImageUrl(cat.image)}
                            alt={cat.name}
                            className="w-5 h-5 object-cover rounded"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "inline";
                            }}
                          />
                        ) : null}
                        <span
                          className="text-base"
                          style={{ display: cat.image ? "none" : "inline" }}
                        >
                          {getDefaultCategoryIcon(cat.name)}
                        </span>
                      </>
                    )}
                    <span className="font-medium truncate">{cat.name}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Products */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          {/* Search and Customer Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#2E55B9]"
                size={18}
              />
              <input
                type="text"
                placeholder="Search products..."
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#2E55B9] focus:ring-2 focus:ring-[#2E55B9]/20 transition-all duration-200 shadow-lg text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Customer Selection */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  value={selectedCustomer}
                  onChange={setSelectedCustomer}
                  options={customerOptions}
                  placeholder="Select Customer"
                  isSearchable
                  isClearable
                  styles={selectStyles}
                  className="text-sm"
                  formatOptionLabel={(option) => (
                    <div>
                      <div className="font-medium">{option.label}</div>
                      {option.phone && (
                        <div className="text-xs text-gray-500">
                          {option.phone}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>

              {global.myClub?.privacyType == ClubPrivacyTypeEnum.Public && (
                <button
                  onClick={() => setOpenCustomerModal(true)}
                  className="bg-green-500 text-white p-2.5 rounded-xl hover:bg-green-600 transition-colors flex items-center justify-center"
                  title="Add New Customer"
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
            {openCustomerModal && (
              <AddCustomer
                id={undefined}
                open={openCustomerModal}
                setOpen={setOpenCustomerModal}
                onClose={_onCustomerAdded}
                bookingModal={true}
              />
            )}
          </div>

          {/* Customer Info Display with Payment Options */}
          {selectedCustomerData && selectedCustomerData.id && (
            // <Card className="shadow-lg border-0">
            //   <CardContent className="p-3">
            //     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            //       <div className="flex items-center gap-3">
            //         <div className="bg-green-100 p-2 rounded-full">
            //           <User className="text-green-600" size={18} />
            //         </div>
            //         <div>
            //           <h3 className="font-medium text-gray-800">
            //             {selectedCustomerData.name}
            //           </h3>
            //           {selectedCustomerData.phone && (
            //             <div className="flex items-center gap-1 text-sm text-gray-600">
            //               <Phone size={12} />
            //               <span>{selectedCustomerData.phone}</span>
            //             </div>
            //           )}
            //         </div>
            //       </div>


            //       {/* Payment Options */}
            //       <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            //         <div className="relative">
            //           <select
            //             //disabled={false}
            //             value={paymentStatus}
            //             onChange={(e) =>
            //               handlePaymentStatusChange(parseInt(e.target.value))
            //             }
            //             className="text-xs border-2 border-green-200 bg-green-50 rounded-lg px-2 py-2 focus:border-green-400 focus:ring-2 focus:ring-green-200 font-medium"
            //             style={{
            //               background:
            //                 paymentStatus === PaymentStatusEnum.Paid
            //                   ? "#f0fdf4"
            //                   : "white",
            //               borderColor:
            //                 paymentStatus === PaymentStatusEnum.Paid
            //                   ? "#22c55e"
            //                   : "#e5e7eb",
            //             }}
            //           >
            //             <option value={PaymentStatusEnum.Paid}>✓ Paid</option>
            //             <option value={PaymentStatusEnum.Partial_Paid}>
            //               ⏳ Partial Paid
            //             </option>
            //             <option value={PaymentStatusEnum.Un_Paid}>
            //               ❌ Unpaid
            //             </option>
            //           </select>
            //         </div>

            //         {(paymentStatus === PaymentStatusEnum.Paid ||
            //           paymentStatus === PaymentStatusEnum.Partial_Paid) && (
            //           <AppSearchSectionSelect
            //             data={paymentAccounts}
            //             onChange={setSelectedAccount}
            //             placeholder="Select Account"
            //             isSearchable
            //             isClearable
            //             styles={selectStyles}
            //             className="text-xs"
            //           />
            //         )}

            //         {paymentStatus === PaymentStatusEnum.Partial_Paid && (
            //           <input
            //             type="number"
            //             placeholder="Received Amount"
            //             value={receivedAmount}
            //             onChange={(e) =>
            //               handleReceivedAmountChange(e.target.value)
            //             }
            //             className="text-xs border-2 border-gray-200 rounded-lg px-2 py-2 focus:border-[#2E55B9] focus:ring-2 focus:ring-[#2E55B9]/20"
            //             max={total}
            //             min={1}
            //           />
            //         )}
            //       </div>
            //     </div>
            //   </CardContent>
            // </Card>
            <Card className="shadow-lg border-0">
              <CardContent className="p-3">
                <div className="grid grid-cols-5 items-center gap-4">

                  {/* Col 1: User Info */}
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 p-2 rounded-full">
                      <User className="text-green-600" size={18} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800">
                        {selectedCustomerData.name}
                      </h3>
                      {selectedCustomerData.phone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone size={12} />
                          <span>{selectedCustomerData.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Col 2: Order Date */}
                  <div className="space-y-1">
                    <AppInput
                      type={"date"}
                      value={Common.moment(orderDate).utc().local().format("YYYY-MM-DD")}
                      min={getDateRestrictions().minDate}
                      max={getDateRestrictions().maxDate}
                      onChange={(e) => {
                        setOrderDate(e);
                      }}
                    />
                    {/* <p className="text-xs text-gray-500">
                    Available: {getDateRestrictions().minDate} to {getDateRestrictions().maxDate}
                  </p> */}
                  </div>


                  {/* Col 3: Payment Status */}
                  <div className="relative">
                    <select
                      value={paymentStatus}
                      onChange={(e) =>
                        handlePaymentStatusChange(parseInt(e.target.value))
                      }
                      className="text-xs border-2 border-green-200 bg-green-50 rounded-lg px-2 py-2 focus:border-green-400 focus:ring-2 focus:ring-green-200 font-medium w-full"
                      style={{
                        background:
                          paymentStatus === PaymentStatusEnum.Paid ? "#f0fdf4" : "white",
                        borderColor:
                          paymentStatus === PaymentStatusEnum.Paid ? "#22c55e" : "#e5e7eb",
                      }}
                    >
                      <option value={PaymentStatusEnum.Paid}>✓ Paid</option>
                      <option value={PaymentStatusEnum.Partial_Paid}>⏳ Partial Paid</option>
                      <option value={PaymentStatusEnum.Un_Paid}>❌ Unpaid</option>
                    </select>
                  </div>

                  {/* Col 4: Account Select (conditional) */}
                  <div>
                    {(paymentStatus === PaymentStatusEnum.Paid ||
                      paymentStatus === PaymentStatusEnum.Partial_Paid) && (
                        <AppSearchSectionSelect
                          data={paymentAccounts}
                          onChange={setSelectedAccount}
                          placeholder="Select Account"
                          isSearchable
                          isClearable
                          styles={selectStyles}
                          className="text-xs"
                        />
                      )}
                  </div>

                  {/* Col 5: Received Amount (conditional) */}
                  <div>
                    {paymentStatus === PaymentStatusEnum.Partial_Paid && (
                      <input
                        type="number"
                        placeholder="Received Amount"
                        value={receivedAmount}
                        onChange={(e) => handleReceivedAmountChange(e.target.value)}
                        className="text-xs border-2 border-gray-200 rounded-lg px-2 py-2 focus:border-[#2E55B9] focus:ring-2 focus:ring-[#2E55B9]/20 w-full"
                        max={total}
                        min={1}
                      />
                    )}
                  </div>


                </div>
              </CardContent>
            </Card>


          )}

          {/* Products Grid */}
          <Card className="flex-1 shadow-xl border-0">
            <CardContent className="p-4">
              {productsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Loader className="animate-spin h-12 w-12 text-[#2E55B9] mx-auto mb-4" />
                    <p className="text-gray-600">Loading products...</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      disabled={product.stock === 0}
                      className={`group relative p-3 border-2 rounded-xl text-left transition-all duration-200 bg-white hover:shadow-xl
                        ${product.stock === 0
                          ? "opacity-50 cursor-not-allowed border-red-200"
                          : "hover:border-[#2E55B9] hover:scale-105 hover:bg-gradient-to-br hover:from-white hover:to-blue-50"
                        }`}
                    >
                      {/* Top Badge for Display on Top products */}
                      {/* {product.isdisplayontop && (
                        <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          <Star size={8} />
                          Top
                        </div>
                      )} */}

                      {/* Low Stock Warning */}
                      {product.isLowStock && product.stock > 0 && (
                        <div className="absolute -top-2 -left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          Low
                        </div>
                      )}

                      <div className="text-center mb-2">
                        {product.image ? (
                          <img
                            src={getImageUrl(product.image)}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg mx-auto"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "inline";
                            }}
                          />
                        ) : null}
                        <span
                          className="text-3xl"
                          style={{ display: product.image ? "none" : "inline" }}
                        >
                          {getDefaultProductIcon(product.categoryName)}
                        </span>
                      </div>

                      <div className="text-xs font-bold text-gray-800 group-hover:text-[#2E55B9] truncate text-center mb-2">
                        {product.name}
                      </div>

                      <div className="text-center">
                        {/* Enhanced Price Display with Special Price Logic */}
                        <div className="mb-2">
                          {product.special_price > 0 ? (
                            <div className="space-y-1">
                              <div className="relative">
                                <span className="text-xs text-gray-400 line-through decoration-red-500 decoration-2">
                                  PKR {product.regular_price}
                                </span>
                                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1 rounded-full">
                                  SALE
                                </div>
                              </div>
                              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1 rounded-lg">
                                <div className="text-sm font-bold">
                                  PKR {product.special_price}
                                </div>
                                {/* <div className="text-xs opacity-90">
                                  Save {product.regular_price - product.special_price}
                                </div> */}
                              </div>
                            </div>
                          ) : (
                            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1 rounded-lg">
                              <div className="text-sm font-bold">
                                PKR {product.regular_price}
                              </div>
                            </div>
                          )}
                        </div>

                        <div
                          className={`text-xs flex items-center justify-center gap-1 ${getStockColor(
                            product.stock,
                            product.isLowStock
                          )}`}
                        >
                          <Package size={10} />
                          <span className="font-medium">
                            {product.stock === 0
                              ? "Out of Stock"
                              : `${product.stock} left`}
                          </span>
                        </div>
                      </div>

                      {product.stock > 0 && (
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <div className="bg-[#2E55B9] text-white p-1 rounded-full shadow-lg">
                            <Plus size={12} />
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar - Cart */}
        <div className="lg:col-span-1">
          <Card className="h-fullx shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-[#2E55B9] to-[#4A73D6] text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShoppingCart size={18} />
                Cart ({cart.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 flex flex-col h-full">
              <div className="flex-1 space-y-2 max-h-80 overflow-y-auto">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 border-2 rounded-lg p-2 hover:border-[#2E55B9] transition-all duration-200"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-xs text-gray-800 truncate">
                          {item.name}
                        </div>
                        <div className="text-xs text-green-600 font-bold">
                          PKR {item.price} × {item.quantity}
                        </div>
                        <div
                          className={`text-xs flex items-center gap-1 mt-1 ${getStockColor(
                            item.stock,
                            item.isLowStock
                          )}`}
                        >
                          <Package size={8} />
                          {item.stock} in stock
                        </div>
                      </div>
                      <button
                        onClick={() => removeItemFromCart(item.id)}
                        className="text-red-500 hover:bg-red-50 rounded-full p-1 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => removeFromCart(item)}
                          className="w-6 h-6 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-6 text-center font-bold text-[#2E55B9] text-xs">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => addToCart(item)}
                          className="w-6 h-6 flex items-center justify-center text-[#2E55B9] hover:bg-[#2E55B9]/10 rounded-full transition-colors"
                          disabled={item.quantity >= item.stock}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-xs font-bold text-gray-800">
                        PKR {(item.price * item.quantity).toFixed(0)}
                      </div>
                    </div>
                  </div>
                ))}

                {cart.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    <ShoppingCart
                      size={40}
                      className="mx-auto mb-4 text-gray-400"
                    />
                    <p className="text-sm">Cart is empty</p>
                    <p className="text-xs text-gray-400">
                      Add items to get started
                    </p>
                  </div>
                )}
              </div>

              {/* Discount Amount  */}

              <div className="mt-2">
                <input
                  type="number"
                  placeholder="Discount Amount"
                  value={discountAmount}
                  onChange={(e) => handleDiscountAmountChange(e.target.value)}
                  className="text-xs border-2 border-gray-200 rounded-lg px-2 py-2 focus:border-[#2E55B9] focus:ring-2 focus:ring-[#2E55B9]/20 w-full"
                  max={total - 1}
                  min={1}
                />
              </div>

              {/* Order Summary & Actions */}
              <div className="mt-4 space-y-3">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl border-2">
                  <div className="flex items-center justify-between font-bold">
                    <span className="text-gray-700 text-sm">Total:</span>
                    <span className="text-[#2E55B9] text-lg">
                      PKR {total.toFixed(0)}
                    </span>
                  </div>

                  {selectedCustomerData && selectedCustomerData.id && (
                    <div className="text-xs text-center mt-2 text-gray-600">
                      Payment: {getPaymentStatusName(paymentStatus)}
                      {selectedAccount && (
                        <span> via {selectedAccount.label}</span>
                      )}
                      {discountAmount > 0 && (
                        <div className="text-xs mt-1 bg-yellow-100 p-1 rounded">
                          Discount: PKR {parseFloat(discountAmount).toFixed(0)} |
                          {" "}
                          Remaining after discount: PKR{" "}
                          {(total - parseFloat(discountAmount || 0)).toFixed(0)}
                        </div>
                      )}


                      {paymentStatus === PaymentStatusEnum.Partial_Paid &&
                        receivedAmount && (
                          <div className="text-xs mt-1 bg-yellow-100 p-1 rounded">
                            {/* Received: PKR {receivedAmount} | Remaining: PKR{" "}
                            {(total - parseFloat(receivedAmount || 0)).toFixed(
                              0
                            )} */}

                            Received: PKR {parseFloat(receivedAmount).toFixed(0)} |{" "}
                            Remaining after payment + discount: PKR{" "}
                            {(() => {
                              const remaining =
                                total -
                                (parseFloat(discountAmount || 0) + parseFloat(receivedAmount || 0));

                              if (remaining < 0) {
                                toast.error("Discount + Payment cannot exceed total amount!");
                                return 0;
                              }

                              return remaining.toFixed(0);
                            })()}
                          </div>
                        )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">

                  {selectedCustomer && paymentStatus !== PaymentStatusEnum.Un_Paid && (
                    <div className="flex justify-center">
                      <div className="w-28 h-28 relative flex items-center justify-center border border-dashed rounded-lg">
                        {attachment ? (
                          <div className="relative w-full h-full flex items-center justify-center">
                            {/* Local preview */}

                            <a
                              href={URL.createObjectURL(attachment)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:opacity-80 transition inline-block"
                            >
                              <img
                                src={URL.createObjectURL(attachment)}
                                alt="Receipt"
                                className="max-w-full max-h-full rounded"
                              />
                            </a>
                            {/* Buttons */}
                            <div className="absolute top-1 right-1 flex space-x-1">

                              {/* Remove */}
                              <button
                                type="button"
                                className="bg-red-100 rounded-full p-1 shadow"
                                onClick={() => setAttachment(null)}
                              >
                                <span className="text-red-500 text-xs">X</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Placeholder (jab image abhi select nahi hui)
                          <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer text-gray-400 text-xs">
                            <ImagePlus className="w-6 h-6 mb-1" />
                            Payment Receipt
                            <br />
                            <span className="text-center">(If Any)</span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setAttachment(file);
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => handlePlaceOrder(false)}
                    className="w-full bg-gradient-to-r from-[#2E55B9] to-[#4A73D6] text-white p-2.5 rounded-lg hover:from-[#2E55B9]/90 hover:to-[#4A73D6]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-lg text-sm"
                    disabled={cart.length === 0}
                  >
                    <CreditCard size={16} />
                    Place Order
                  </button>

                  <button
                    onClick={() => handlePlaceOrder(true)}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white p-2.5 rounded-lg hover:from-green-500/90 hover:to-green-600/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-lg text-sm"
                    disabled={cart.length === 0}
                  >
                    <Receipt size={16} />
                    Place with Slip
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default POSSystem;
